import { Reference, useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { gql } from '~/__generated__'
import { GetPlaylistVideosQuery } from '~/__generated__/graphql'

const REMOVE_PLAYLIST_VIDEO = gql(`
  mutation removePlaylistVideo($input: RemovePlaylistsVideoMutationInput!) {
    removePlaylistsVideo(input: $input) {
      clientMutationId
      errors
      playlistsVideo {
        id
      }
    }
  }
`)

export const useRemovePlaylistVideo = () => {
  const [removePlaylistVideoMutation, { loading, error }] = useMutation(REMOVE_PLAYLIST_VIDEO)

  const removePlaylistVideo = useCallback(
    async (playlistId: string, videoId: string) => {
      const result = await removePlaylistVideoMutation({
        variables: {
          input: {
            playlistId,
            videoId,
          },
        },
        optimisticResponse: {
          removePlaylistsVideo: {
            __typename: 'RemovePlaylistsVideoMutationPayload',
            clientMutationId: null,
            errors: [],
            playlistsVideo: {
              __typename: 'PlaylistsVideo',
              id: `temp-remove-${playlistId}-${videoId}`,
            },
          },
        },
        update: (cache) => {
          cache.modify({
            id: `Playlist:${playlistId}`,
            fields: {
              videos(existing, { readField }) {
                const connection = existing as GetPlaylistVideosQuery['playlist']['videos']
                if (!connection?.nodes) return existing
                return {
                  ...connection,
                  nodes: connection.nodes.filter((ref) => readField('id', ref) !== videoId),
                  totalCount: (connection.totalCount ?? 0) - 1,
                }
              },
            },
          })
          cache.modify({
            id: `OriginalVideo:${videoId}`,
            fields: {
              playlistsVideos(existing = [], { readField }) {
                return (existing as Reference[]).filter((ref) => {
                  const playlist = readField('playlist', ref) as Reference | undefined
                  if (!playlist) return true
                  return readField('id', playlist) !== playlistId
                })
              },
            },
          })
          cache.gc()
        },
      })

      if (
        result.data?.removePlaylistsVideo?.errors &&
        result.data.removePlaylistsVideo.errors.length > 0
      ) {
        throw new Error(result.data.removePlaylistsVideo.errors.join(', '))
      }
    },
    [removePlaylistVideoMutation],
  )

  return {
    removePlaylistVideo,
    loading,
    error,
  }
}
